import type { SeverityTier } from '@/types'

export const SEV_COLOR: Record<string, string> = {
  '0':  '#e84040',
  '1':  '#f5a623',
  '2':  '#34c95f',
  'null': '#2a2a38',
}

export const SEV_LABEL: Record<string, string> = {
  '0': 'critical',
  '1': 'warning',
  '2': 'healthy',
  'null': 'no data',
}

export const SEV_BG: Record<string, string> = {
  '0':  'rgba(232,64,64,.12)',
  '1':  'rgba(245,166,35,.12)',
  '2':  'rgba(52,201,95,.12)',
  'null': 'rgba(255,255,255,.04)',
}

export const SEV_BORDER: Record<string, string> = {
  '0':  'rgba(232,64,64,.3)',
  '1':  'rgba(245,166,35,.3)',
  '2':  'rgba(52,201,95,.3)',
  'null': 'rgba(255,255,255,.08)',
}

// Pipeline layer labels and colours — 5 layers per hex
export const PIPELINE_LAYERS = [
  { key: 'http',   label: 'A. HTTP',   color: '#4d9de0' },
  { key: 'demux',  label: 'B. DEMUX',  color: '#a78bfa' },
  { key: 'decode', label: 'C. DECODE', color: '#f0827d' },
  { key: 'render', label: 'D. RENDER', color: '#60bfa8' },
  { key: 'flow',   label: 'E. FLOW',   color: '#f5a623' },
] as const

// Scale factor for each successive layer (outermost = 1.0)
export const LAYER_SCALES = [1.0, 0.78, 0.58, 0.40, 0.24]

export const METRIC_COLOURS = {
  // Deliberately avoids the severity palette (green/yellow/red/grey).
  mos:  '#5B8CFF',  // blue
  rbf:  '#22D3EE',  // cyan
  std:  '#A78BFA',  // violet
  bdr:  '#F472B6',  // pink
  g2g:  '#2DD4BF',  // teal
  loss: '#94A3B8',  // slate
  buf:  '#60A5FA',  // light blue
}

export function getSevColor(sev: SeverityTier | null): string {
  return SEV_COLOR[String(sev)] ?? SEV_COLOR['null']
}

export function getSevLabel(sev: SeverityTier | null): string {
  return SEV_LABEL[String(sev)] ?? 'no data'
}

export const CONTINENTS = ['EU', 'NA', 'SA', 'AS', 'AF', 'OC']

export const REGION_LABEL: Record<string, string> = {
  EU: 'Europe',
  NA: 'North America',
  SA: 'South America',
  AS: 'Asia',
  AF: 'Africa',
  OC: 'Oceania',
  OT: 'Oceania',

  // Demo-only country display names
  GB: 'Great Britain',
  ID: 'Indonesia',
  VN: 'Vietnam',
  KR: 'Korea',
}

export function getRegionLabel(code: string | null | undefined): string {
  if (!code) return '—'
  return REGION_LABEL[code] ?? code
}

const COUNTRY_TO_CONTINENT = {
  // Europe
  AD: 'Europe', AL: 'Europe', AT: 'Europe', BA: 'Europe', BE: 'Europe',
  BG: 'Europe', BY: 'Europe', CH: 'Europe', CY: 'Europe', CZ: 'Europe',
  DE: 'Europe', DK: 'Europe', EE: 'Europe', ES: 'Europe', FI: 'Europe',
  FO: 'Europe', FR: 'Europe', GB: 'Europe', GG: 'Europe', GI: 'Europe',
  GR: 'Europe', HR: 'Europe', HU: 'Europe', IE: 'Europe', IM: 'Europe',
  IS: 'Europe', IT: 'Europe', JE: 'Europe', LI: 'Europe', LT: 'Europe',
  LU: 'Europe', LV: 'Europe', MC: 'Europe', MD: 'Europe', ME: 'Europe',
  MK: 'Europe', MT: 'Europe', NL: 'Europe', NO: 'Europe', PL: 'Europe',
  PT: 'Europe', RO: 'Europe', RS: 'Europe', RU: 'Europe', SE: 'Europe',
  SI: 'Europe', SK: 'Europe', SM: 'Europe', UA: 'Europe', VA: 'Europe',
  XK: 'Europe', AX: 'Europe',

  // North America (incl. Central America & Caribbean per ISO grouping)
  AG: 'North America', AI: 'North America', AW: 'North America',
  BB: 'North America', BL: 'North America', BM: 'North America',
  BQ: 'North America', BS: 'North America', BZ: 'North America',
  CA: 'North America', CR: 'North America', CU: 'North America',
  CW: 'North America', DM: 'North America', DO: 'North America',
  GD: 'North America', GL: 'North America', GP: 'North America',
  GT: 'North America', HN: 'North America', HT: 'North America',
  JM: 'North America', KN: 'North America', KY: 'North America',
  LC: 'North America', MF: 'North America', MQ: 'North America',
  MS: 'North America', MX: 'North America', NI: 'North America',
  PA: 'North America', PM: 'North America', PR: 'North America',
  SV: 'North America', SX: 'North America', TC: 'North America',
  TT: 'North America', US: 'North America', VC: 'North America',
  VG: 'North America', VI: 'North America',

  // South America
  AR: 'South America', BO: 'South America', BR: 'South America',
  CL: 'South America', CO: 'South America', EC: 'South America',
  FK: 'South America', GF: 'South America', GY: 'South America',
  PE: 'South America', PY: 'South America', SR: 'South America',
  UY: 'South America', VE: 'South America',

  // Asia
  AF: 'Asia', BD: 'Asia', BN: 'Asia', BT: 'Asia', CN: 'Asia',
  HK: 'Asia', ID: 'Asia', IN: 'Asia', JP: 'Asia', KG: 'Asia',
  KH: 'Asia', KP: 'Asia', KR: 'Asia', KZ: 'Asia', LA: 'Asia',
  LK: 'Asia', MM: 'Asia', MN: 'Asia', MO: 'Asia', MV: 'Asia',
  MY: 'Asia', NP: 'Asia', PH: 'Asia', PK: 'Asia', SG: 'Asia',
  TH: 'Asia', TJ: 'Asia', TL: 'Asia', TM: 'Asia', TW: 'Asia',
  UZ: 'Asia', VN: 'Asia',

  // Middle East (broken out from ISO's "Asia" because you list it separately)
  AE: 'Middle East', BH: 'Middle East', IL: 'Middle East',
  IQ: 'Middle East', IR: 'Middle East', JO: 'Middle East',
  KW: 'Middle East', LB: 'Middle East', OM: 'Middle East',
  PS: 'Middle East', QA: 'Middle East', SA: 'Middle East',
  SY: 'Middle East', TR: 'Middle East', YE: 'Middle East',

  // Africa
  AO: 'Africa', BF: 'Africa', BI: 'Africa', BJ: 'Africa', BW: 'Africa',
  CD: 'Africa', CF: 'Africa', CG: 'Africa', CI: 'Africa', CM: 'Africa',
  CV: 'Africa', DJ: 'Africa', DZ: 'Africa', EG: 'Africa', EH: 'Africa',
  ER: 'Africa', ET: 'Africa', GA: 'Africa', GH: 'Africa', GM: 'Africa',
  GN: 'Africa', GQ: 'Africa', GW: 'Africa', KE: 'Africa', KM: 'Africa',
  LR: 'Africa', LS: 'Africa', LY: 'Africa', MA: 'Africa', MG: 'Africa',
  ML: 'Africa', MR: 'Africa', MU: 'Africa', MW: 'Africa', MZ: 'Africa',
  NA: 'Africa', NE: 'Africa', NG: 'Africa', RE: 'Africa', RW: 'Africa',
  SC: 'Africa', SD: 'Africa', SH: 'Africa', SL: 'Africa', SN: 'Africa',
  SO: 'Africa', SS: 'Africa', ST: 'Africa', SZ: 'Africa', TD: 'Africa',
  TG: 'Africa', TN: 'Africa', TZ: 'Africa', UG: 'Africa', YT: 'Africa',
  ZA: 'Africa', ZM: 'Africa', ZW: 'Africa',

  // Oceania
  AS: 'Oceania', AU: 'Oceania', CK: 'Oceania', FJ: 'Oceania',
  FM: 'Oceania', GU: 'Oceania', KI: 'Oceania', MH: 'Oceania',
  MP: 'Oceania', NC: 'Oceania', NF: 'Oceania', NR: 'Oceania',
  NU: 'Oceania', NZ: 'Oceania', PF: 'Oceania', PG: 'Oceania',
  PN: 'Oceania', PW: 'Oceania', SB: 'Oceania', TK: 'Oceania',
  TO: 'Oceania', TV: 'Oceania', VU: 'Oceania', WF: 'Oceania',
  WS: 'Oceania',
};
export function countryToContinent(country: string): string {
  const key = (country ?? '').trim()
  const continent = COUNTRY_TO_CONTINENT[key as keyof typeof COUNTRY_TO_CONTINENT]
  return continent ?? 'Unknown'
}

// Reverse lookup: continent display name → array of country codes.
// Built once at module load from COUNTRY_TO_CONTINENT.
const CONTINENT_TO_COUNTRIES: Record<string, string[]> = (() => {
  const out: Record<string, string[]> = {}
  for (const [cc, continent] of Object.entries(COUNTRY_TO_CONTINENT)) {
    if (!out[continent]) out[continent] = []
    out[continent].push(cc)
  }
  return out
})()

export function continentToCountries(continent: string): string[] {
  return CONTINENT_TO_COUNTRIES[continent] ?? []
}

// Optional: list of all known continent display names
export const CONTINENT_NAMES = Object.keys(CONTINENT_TO_COUNTRIES)